import Alert from "@mui/material/Alert";
import Chip from "@mui/material/Chip";
import Stack from "@mui/material/Stack";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import Typography from "@mui/material/Typography";
import { LoadingState } from "../../../../../components/primitives/feedback/LoadingState";
import {
  DataSequence,
  type DataSequenceColumn,
} from "../../../../../components/primitives/data-display/DataSequence";
import { DocumentLink } from "../../../../../components/domain/data-display/DocumentLink";
import { Note } from "../../../../../components/primitives/data-display/Note";
import { Statistic } from "../../../../../components/primitives/data-display/Statistic";
import { TaxonomyUrnBadge } from "../../../../../components/domain/data-display/TaxonomyUrnBadge";
import { getBatchJobItemRowId } from "../../panel/batch_job_item_rows";
import {
  DOCUMENT_INDEXING_REFRESH_TAB_ORDER,
  DOCUMENT_INDEXING_REFRESH_TABS,
} from "./document_indexing_refresh_tabs";
import type {
  DocumentIndexingRefreshPanelSummary,
  DocumentIndexingRefreshRow,
  UseDocumentIndexingRefreshPanelResult,
} from "./useDocumentIndexingRefreshPanel";

/** The fields as badges, or a plain note when there are none. */
function FieldBadges({ urns }: { urns: string[] }) {
  return (
    <Stack direction="row" gap={0.5} flexWrap="wrap">
      {urns.map((urn) => (
        <TaxonomyUrnBadge key={urn} urn={urn} />
      ))}
    </Stack>
  );
}

/** The header's counts, and the fields gained across the job with how many documents gained each. */
function SummaryHeader({ summary }: { summary: DocumentIndexingRefreshPanelSummary | null }) {
  if (!summary) {
    return <Note>The summary appears once the job has finished.</Note>;
  }
  return (
    <Stack gap={2}>
      <Stack direction="row" gap={4} flexWrap="wrap">
        <Statistic label="gained fields">{summary.fieldsAddedCount}</Statistic>
        <Statistic label="no new fields">{summary.noNewFieldsCount}</Statistic>
        <Statistic label="still without fields">
          <Typography
            component="span"
            variant="inherit"
            color={summary.withoutFields > 0 ? "warning.main" : "text.primary"}
          >
            {summary.withoutFields}
          </Typography>
        </Statistic>
        <Statistic label="subjects added">{summary.subjectsAdded}</Statistic>
      </Stack>

      <Stack gap={1}>
        <Typography variant="caption" color="text.secondary">
          Fields gained, by number of documents
        </Typography>
        {summary.fieldsGained.length === 0 ? (
          <Note>No document gained a field.</Note>
        ) : (
          <Stack direction="row" gap={1.5} flexWrap="wrap">
            {summary.fieldsGained.map((field) => (
              <Stack key={field.urn} direction="row" gap={0.5} alignItems="center">
                <TaxonomyUrnBadge urn={field.urn} />
                <Typography variant="caption" color="text.secondary">
                  {field.documents} {field.documents === 1 ? "document" : "documents"}
                </Typography>
              </Stack>
            ))}
          </Stack>
        )}
      </Stack>
    </Stack>
  );
}

/** What a row's document gained: its new fields and subjects, or the failure it hit. */
function GainedCell({ row }: { row: DocumentIndexingRefreshRow }) {
  const { result, item } = row;
  if (!result) {
    return item.error ? (
      <Typography variant="caption" color="error">
        {item.error}
      </Typography>
    ) : (
      <>—</>
    );
  }
  return (
    <Stack gap={0.5} alignItems="flex-start">
      {result.fieldsAdded.length > 0 ? (
        <FieldBadges urns={result.fieldsAdded} />
      ) : (
        <Typography variant="caption" color="text.secondary">
          No new fields
        </Typography>
      )}
      {result.subjectsAdded > 0 && (
        <Typography variant="caption" color="text.secondary">
          +{result.subjectsAdded} {result.subjectsAdded === 1 ? "subject" : "subjects"}
        </Typography>
      )}
    </Stack>
  );
}

/** The document's fields once the refresh has run, or a warning when it has none. */
function FieldsNowCell({ row }: { row: DocumentIndexingRefreshRow }) {
  const { result } = row;
  if (!result) return <>—</>;
  if (result.fieldsAfter.length === 0) {
    return <Chip label="No fields" color="warning" size="small" variant="outlined" />;
  }
  return <FieldBadges urns={result.fieldsAfter} />;
}

export interface DocumentIndexingRefreshPanelBodyProps {
  panel: UseDocumentIndexingRefreshPanelResult;
}

/** Presentational document_indexing_refresh results panel: a summary of what the run gained, then one
 *  tab per outcome, each a paginated list of documents with the fields they gained and the fields
 *  they now have. Read-only. */
export function DocumentIndexingRefreshPanelBody({ panel }: DocumentIndexingRefreshPanelBodyProps) {
  const columns: DataSequenceColumn<DocumentIndexingRefreshRow>[] = [
    {
      key: "subject",
      header: "Document",
      width: 260,
      cardRole: "primary",
      cell: (row) => <DocumentLink title={row.item.subjectTitle} url={row.item.subjectUrl} />,
    },
    {
      key: "gained",
      header: "Gained",
      cell: (row) => <GainedCell row={row} />,
    },
    {
      key: "fieldsNow",
      header: "Fields now",
      cell: (row) => <FieldsNowCell row={row} />,
    },
  ];

  return (
    <Stack gap={2}>
      <SummaryHeader summary={panel.summary} />

      <Tabs
        value={panel.tabs.activeTab}
        onChange={(_e, v: string) => panel.tabs.setActiveTab(v as typeof panel.tabs.activeTab)}
        sx={{ borderBottom: 1, borderColor: "divider" }}
        variant="scrollable"
      >
        {DOCUMENT_INDEXING_REFRESH_TAB_ORDER.map((key) => (
          <Tab key={key} label={DOCUMENT_INDEXING_REFRESH_TABS[key].label} value={key} />
        ))}
      </Tabs>

      {panel.list.error && <Alert severity="error">{panel.list.error}</Alert>}

      {panel.list.loading ? (
        <LoadingState />
      ) : (
        <DataSequence
          columns={columns}
          rows={panel.list.rows}
          getRowId={getBatchJobItemRowId}
          emptyMessage="No documents in this list."
          pagination={panel.list.pagination}
        />
      )}
    </Stack>
  );
}
